"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { paginaDoPixel } from "@/lib/camp/meta";
import { carregarPixel, configurarPixel, escolhaDosCookies, guardarEscolhaDosCookies, medirPaginaNoMeta } from "@/lib/camp/pixelNoNavegador";

/**
 * O Meta Pixel e o aviso de cookies das páginas de evento (brief de
 * 30/10/2026). Nada do pixel carrega antes do "Aceitar". Quem recusa segue com
 * o PostHog e a inscrição iguais. Sem `META_PIXEL_ID`, nem o aviso aparece.
 */
export default function PixelDoCamp({ pixelId, slugs, testeDoMeta }: { pixelId: string | null; slugs: readonly string[]; testeDoMeta: string | null }) {
  const pathname = usePathname();
  const pagina = pixelId ? paginaDoPixel(pathname, slugs) : null;
  const [escolha, setEscolha] = useState<"sim" | "nao" | null | undefined>(undefined);
  const medida = useRef<string | null>(null);

  useEffect(() => {
    // O cookie só existe no navegador: lido depois de montar, para o HTML estático não divergir.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEscolha(escolhaDosCookies());
  }, []);

  useEffect(() => {
    if (!pixelId || !pagina || escolha !== "sim") return;
    const endereco = `${pathname}${window.location.search}`;
    if (medida.current === endereco) return;
    medida.current = endereco;
    const comToken = pagina === "confirmacao" || new URLSearchParams(window.location.search).has("reserva");
    configurarPixel(pixelId, comToken ? pathname.replace(/\/[0-9a-f-]{36}\/?$/i, "") : null, testeDoMeta);
    if (!comToken) carregarPixel(pixelId);
    medirPaginaNoMeta("PageView");
    if (pagina === "landing") medirPaginaNoMeta("ViewContent", { content_ids: [pathname.split("/")[2]], content_type: "product" });
  }, [pixelId, pagina, escolha, pathname, testeDoMeta]);

  const aberto = !!pagina && escolha === null;

  // O aviso nunca cobre a barra fixa de inscrição (31/10/2026): com o aviso
  // aberto, a landing do anúncio ficou sem o "Quero minha vaga" de baixo e as
  // idas ao formulário caíram a zero. Se a página tem barra (`data-barra-fixa`),
  // o aviso sobe e fica em cima dela. A altura do aviso vai para
  // `--aviso-cookies`, para o botão do WhatsApp subir junto e não tapar o aviso.
  const [barra, setBarra] = useState(0);
  const aviso = useRef<HTMLDivElement>(null);
  // Antes de pintar, para o aviso não aparecer um quadro em cima da barra.
  // A barra é procurada de novo a cada mudança na página: num celular lento a
  // página chega por partes, e a barra pode aparecer depois do aviso.
  useLayoutEffect(() => {
    if (!aberto) return;
    const raiz = document.documentElement;
    let daBarra: HTMLElement | null = null;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => mede()) : null;
    const mede = () => {
      const agora = document.querySelector<HTMLElement>("[data-barra-fixa]");
      if (agora !== daBarra) {
        if (daBarra) ro?.unobserve(daBarra);
        if (agora) ro?.observe(agora);
        daBarra = agora;
      }
      setBarra(daBarra?.offsetHeight ?? 0);
      if (aviso.current) raiz.style.setProperty("--aviso-cookies", `${aviso.current.offsetHeight}px`);
    };
    mede();
    if (ro && aviso.current) ro.observe(aviso.current);
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(mede) : null;
    mo?.observe(document.body, { childList: true, subtree: true });
    return () => {
      mo?.disconnect();
      ro?.disconnect();
      raiz.style.removeProperty("--aviso-cookies");
    };
  }, [aberto, pathname]);

  if (!aberto) return null;

  const responder = (e: "sim" | "nao") => {
    try {
      guardarEscolhaDosCookies(e);
    } catch {
      // sem cookie, o aviso volta na próxima página
    }
    setEscolha(e);
  };

  return (
    <div ref={aviso} role="region" aria-label="Aviso de cookies" style={{ bottom: barra }} className="fixed inset-x-0 bottom-0 z-50 border-t border-[#dde3ee] bg-white px-4 py-2.5 shadow-[0_-4px_16px_rgba(11,26,51,0.12)]">
      <div className="mx-auto flex max-w-[760px] flex-col gap-2">
        <p className="m-0 text-[13px] leading-snug text-[#0b1a33]">Usamos cookies para entender como você chegou até aqui e melhorar nossos anúncios.</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => responder("sim")} className="min-h-[44px] rounded-xl bg-[#002868] px-4 text-[14px] font-bold text-white">
            Aceitar
          </button>
          <button type="button" onClick={() => responder("nao")} className="min-h-[44px] rounded-xl border border-[#c6cfde] bg-white px-4 text-[14px] font-bold text-[#0b1a33]">
            Recusar
          </button>
          <a href={`/camp/privacidade?evento=${pathname.split("/")[2]}`} className="px-1 text-[13px] font-semibold text-[#002868]">
            Política de privacidade
          </a>
        </div>
      </div>
    </div>
  );
}
